import { Autocomplete, Box, Card, Chip, Grid, TextField, Tabs, Tab, Divider } from "@mui/material";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Swal from "sweetalert2";
import MediaSelector from "./MediaSelector";
import { useSoftUIController } from "hooks/context";
import SoftSelect from "components/SoftComponents/SoftSelect";
import ScheduleInterface from "./ScheduleInterface";
import { useNavigate } from "react-router-dom";

import DisruptionForm from "./Forms/disruption";
import PlannedNoticeForm from "./Forms/planned";
import ServiceAlertForm from "./Forms/service";
import VolumeSliderForm from "./Forms/volume_slider";
import LightingSliderForm from "./Forms/lighting";
import { useMutation } from "@tanstack/react-query";



// 1. Helper to render error text
const ErrorMessage = ({ error }) => {
    if (!error) return null;
    return (
        <SoftTypography variant="caption" color="error" fontWeight="medium" sx={{ display: 'block', mt: 0.5 }}>
            {error.message}
        </SoftTypography>
    );
};


function CampaignEdit({ campaign, campaignTypes, tagOptions, saveForm }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;
    const navigate = useNavigate();
    const formRef = useRef(null);

    const canCampaignsEdit = userPermissions.indexOf('campaign_manager:edit') >= 0;

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        clearErrors
    } = useForm({
        defaultValues: {
            ...campaign,
            contentId: campaign?.contentId?._id || campaign?.contentId || null,
            formData: campaign?.formData || { title: '', description: '' }
        }
    });

    const scheduleType = watch("scheduleType") || campaign?.scheduleType;
    const currentCampaignTypeId = watch("campaignTypeId");
    const selectedContentId = watch("contentId") || campaign?.contentId?._id || campaign?.contentId;

    // 3. Derived object (Memoize this for performance)
    const campaignTypeObj = useMemo(() => {
        return campaignTypes?.find(type => type._id == currentCampaignTypeId);
    }, [campaignTypes, currentCampaignTypeId]);

    useEffect(() => {
        if (typeof campaignTypeObj == 'undefined') {
            navigate('/campaigns', { replace: true });
        }
    }, [campaignTypeObj, selectedOrg]);

    // 4. Update dependent fields when the type changes
    useEffect(() => {
        if (campaignTypeObj?.expectedDocumentType) {
            setValue("mediaType", campaignTypeObj.expectedDocumentType);
        }

        if (campaignTypeObj?.expectedDocumentType == "form") {
            setValue("formData.type", campaignTypeObj.campaign_type);
        }

    }, [campaignTypeObj, setValue]);

    const contentType = campaignTypeObj?.expectedDocumentType;

    const triggerFormSubmit = () => {
        if (formRef.current) {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        }
    };

    const saveMutation = useMutation({
        mutationFn: async (payload) => {
            return await saveForm(payload);
        },
        onMutate: () => {
            Swal.fire({
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:26x">Saving...</div>',
                // html: 'Please wait while your campaign is saved.',
                allowEscapeKey: false,
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });
        },
        onSuccess: () => {
            Swal.close();
            Swal.fire({
                icon: 'success',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:26px">Saved!</div>',
                showConfirmButton: false,
                timer: 2000
            }).then(() => {
                navigate(-1);
            });
        },
        onError: (error) => {
            Swal.fire({
                icon: 'error',
                title: '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:26px">Error Saving Campaign</div>',
                text: error?.message || '<div style="font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px">Something went wrong while trying to save.</div>',
                confirmButtonColor: '#d33',
            });
        }
    });

    function submitForm(data) {
        if (canCampaignsEdit) {
            const payload = { ...data };

            // Clean up based on Schema requirements
            if (payload.mediaType === 'media') {
                payload.formData = undefined; // Use undefined so it doesn't send {}
            } else {
                payload.contentId = undefined; // Use undefined so it doesn't send null/invalid ObjectId
            }

            saveMutation.mutate(payload);
        }
    }

    const schedule_options = [
        { value: "recurring", label: "Regularly throughout the week" },
        { value: "recurringWithDate", label: "Regularly throughout the week and between a certain date and time" },
        { value: "startEnd", label: "Between a certain date and time" }
    ];

    // GROUPS ADDITION
    const transport_options = [
        {category: 'Group', field: 'group_id', value: 'all_org_sites', id: 'all_org_sites', label: 'All Sites'},
        ...(tagOptions?.deviceGroups || []).filter(dg => dg.type == "campaigns").map(dg => ({
            category: 'Group', field: 'group_id', value: dg.group_id, id: dg.group_id, label: dg.name
        })),
        ...(tagOptions?.tags?.stops || []).map((s) => ({
            category: s?.creation_type?.includes('gtfs') ? 'Stop' : 'Site', field: 'stop_id', value: s.site_id, id: s.site_id, label: (s?.site_name ? `${s.stop_id} - ${s.site_name}` : `${s.stop_id}`)
        })),
        ...(tagOptions?.tags?.routes || []).map((r) => ({
            category: 'Route', field: 'route_id', value: r.route_short_name, label: `Route ${r.route_short_name} - ${r.route_long_name}`
        }))
    ];

    let transport_objects_dict = {};
    transport_options.forEach((row) => {
        transport_objects_dict[row.field + '_' + row.id + '_' + row.value] = row;
    })

    if (campaignTypeObj) {
        return (
            <>
                {/* <SoftBox m={1}>
                    <Card sx={{ overflow: "visible", width: '100%', borderRadius: '16px' }}>
                        <SoftBox mx={2} mt={1} mb={1}>
                            <SoftBox display='flex' flexDirection='row' justifyContent='space-between'>
                                <Grid container>
                                    <Grid item xxs={12}>
                                        <SoftBox height="100%" display="flex" flexDirection="column" justifyContent="center" alignItems={{ xxs: 'center', xs: 'flex-start' }}>
                                            <SoftTypography variant="h5" fontWeight="bold" textAlign="center" sx={{ lineHeight: 2.5 }}>{campaign._id ? "Edit Campaign" : "New Campaign"}</SoftTypography>
                                        </SoftBox>
                                    </Grid>
                                </Grid>
                            </SoftBox>
                        </SoftBox>
                    </Card>
                </SoftBox> */}
                <SoftBox p={1} sx={{ height: `calc(100% - 16px)` }}>
                    <Card sx={{ height: '100%' }}>
                        <SoftBox display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                            <SoftBox flex={1} component="form" ref={formRef} onSubmit={handleSubmit(submitForm)} px={2} py={2} sx={{ overflowY: 'auto' }}>
                                <Grid container spacing={2}>

                                    {/* Campaign Name */}
                                    <Grid item xxs={12} lg={6}>
                                        <SoftBox pl={1} mb={0.5}>
                                            <SoftTypography variant="body3" fontWeight='bold'>Campaign Name</SoftTypography>
                                        </SoftBox>
                                        <SoftInput
                                            placeholder="e.g., Summer Promo"
                                            {...register('name', { required: "Campaign Name is required" })}
                                            error={!!errors.name}
                                            disabled={!canCampaignsEdit}
                                        />
                                        <SoftBox pl={1}>
                                            <ErrorMessage error={errors.name} />
                                        </SoftBox>
                                    </Grid>

                                    {/* Campaign Type */}
                                    <Grid item xxs={12} lg={6}>
                                        <SoftBox pl={1} mb={0.5}>
                                            <SoftTypography variant="body3" fontWeight='bold'>Campaign Type</SoftTypography>
                                        </SoftBox>
                                        <input type="hidden" {...register('campaignTypeId', { required: "Campaign Type ID is missing" })} />
                                        <SoftInput value={campaignTypeObj ? campaignTypeObj.label : "Loading Type..."} disabled />
                                    </Grid>

                                    {/* Tags/Device Filter */}
                                    <Grid item xxs={12}>
                                        <SoftBox pl={1} mb={0.5}>
                                            <SoftTypography variant="body3" fontWeight='bold'>Tags</SoftTypography>
                                        </SoftBox>
                                        <Controller
                                            name="deviceFilter"
                                            control={control}
                                            rules={{ required: "At least one target must be selected" }}
                                            disabled={!canCampaignsEdit}
                                            render={({ field: { onChange, value, ref } }) => {
                                                return (
                                                    <Autocomplete
                                                        multiple
                                                        disabled={!canCampaignsEdit}
                                                        options={transport_options}
                                                        groupBy={(option) => option.category}
                                                        getOptionLabel={(option) => option.label}
                                                        value={value || []}
                                                        onChange={(e, val) => onChange(val)}
                                                        isOptionEqualToValue={(option, val) => option.field === val.field && option.value === val.value}
                                                        renderTags={(tagValue, getTagProps) =>
                                                            tagValue.map((option, index) => {
                                                                const { key, onDelete, ...tagProps } = getTagProps({ index });

                                                                const finalTagProps = !canCampaignsEdit ? { ...tagProps, disabled: false } : { ...tagProps, onDelete };

                                                                let transport_option = transport_objects_dict[option.field + '_' + option.id + '_' + option.value];

                                                                if (transport_option) {
                                                                    return <Chip key={key} variant="outlined" label={`${transport_option.category}: ${transport_option.label}`} {...finalTagProps} />;
                                                                }

                                                                return <Chip key={key} variant="outlined" label={`${option.category}: ${option.label}`} {...finalTagProps} />;
                                                            })
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField {...params} inputRef={ref} variant="outlined" error={!!errors.deviceFilter} />
                                                        )}
                                                        sx={{
                                                            // 1. Fix the main Autocomplete wrapper opacity
                                                            '&.Mui-disabled': {
                                                                opacity: 1,
                                                                WebkitTextFillColor: 'inherit',
                                                            },
                                                            // 2. Fix the text color inside the input field
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                WebkitTextFillColor: '#222222',
                                                                color: '#222222',
                                                            },
                                                            // 3. Fix the Fieldset border color (MUI uses fieldset for outlined variants)
                                                            '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: 'rgba(0, 0, 0, 0.23)', // This resets it to standard normal gray border
                                                            },
                                                            // 4. Fix the label text color
                                                            '& .MuiFormLabel-root.Mui-disabled': {
                                                                color: '#222222',
                                                            },
                                                            // 5. Hide the dropdown arrow completely when disabled to avoid clicking confusion
                                                            '& .MuiAutocomplete-popupIndicator.Mui-disabled': {
                                                                visibility: 'hidden',
                                                            }
                                                        }}
                                                    />
                                                );
                                            }}
                                        />
                                        <SoftBox pl={1}>
                                            <ErrorMessage error={errors.deviceFilter} />
                                        </SoftBox>
                                    </Grid>

                                    {/* CONTENT SECTION */}
                                    <Grid item xxs={12} x4l={5}>
                                        <SoftBox pl={1} mb={0.5}>
                                            <SoftTypography variant="body3" fontWeight='bold'>Content</SoftTypography>
                                        </SoftBox>

                                        <SoftBox p={1} borderRadius="10px" height={{ xxs: 'unset', x4l: contentType != 'form' && selectedContentId ? '100%' : 'unset' }} sx={{ backgroundColor: "#eeeeee", border: (contentType === 'media' && errors.contentId) || (contentType === 'form' && errors.formData) ? "1px solid red" : "none", overflow: "hidden" }}>
                                            <Box py={1}>
                                                <input type="hidden" {...register('mediaType')} />

                                                {contentType === 'media' ? (
                                                    <Controller
                                                        name="contentId"
                                                        control={control}
                                                        rules={{ required: contentType === 'media' ? "Media content must be selected" : false }}
                                                        disabled={!canCampaignsEdit}
                                                        render={() => <MediaSelector {...{ control, selectedOrg, canCampaignsEdit }} />}
                                                    />
                                                ) : (
                                                    <>
                                                        {campaignTypeObj?.campaign_type === 'disruption' && (
                                                            <DisruptionForm register={register} errors={errors} watch={watch} setValue={setValue} />
                                                        )}
                                                        {campaignTypeObj?.campaign_type === 'alerts_l' && (
                                                            <PlannedNoticeForm register={register} errors={errors} watch={watch} setValue={setValue} />
                                                        )}
                                                        {campaignTypeObj?.campaign_type === 'service' && (
                                                            <ServiceAlertForm register={register} errors={errors} watch={watch} setValue={setValue} />
                                                        )}
                                                        {campaignTypeObj?.campaign_type === 'tts' && (
                                                            <VolumeSliderForm register={register} errors={errors} watch={watch} setValue={setValue} />
                                                        )}
                                                        {campaignTypeObj?.campaign_type === 'lighting' && (
                                                            <LightingSliderForm register={register} errors={errors} watch={watch} setValue={setValue} />
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </SoftBox>
                                        <SoftBox pl={1}>
                                            <ErrorMessage error={contentType === 'media' ? errors.contentId : errors.formData?.title} />
                                        </SoftBox>
                                    </Grid>

                                    {/* Scheduling */}
                                    <Grid item xxs={12} x4l={7}>
                                        <SoftBox pl={1} mb={0.5}>
                                            <SoftTypography variant="body3" fontWeight='bold'>Scheduling</SoftTypography>
                                        </SoftBox>
                                        <SoftBox p={2} borderRadius="10px" height={{ xxs: 'unset', x4l: scheduleType === "alwaysOn" ? 'unset' : 'calc(100% - 16px)' }} sx={{ backgroundColor: "#eeeeee" }}>
                                            <SoftBox display="flex" flexDirection={{ xxs: "column", xs: "row" }} justifyContent={{ xxs: 'center', xs: "space-between" }} alignItems="center">
                                                <SoftTypography pb={{ xxs: 1, xs: 0 }} variant="subtitle2" fontWeight="bold">{scheduleType !== "alwaysOn" ? "SCHEDULED" : "ALWAYS SHOWING"}</SoftTypography>
                                                {canCampaignsEdit && (
                                                    <SoftButton
                                                        variant="gradient"
                                                        color="dark"
                                                        onClick={() => setValue("scheduleType", scheduleType === "alwaysOn" ? "recurring" : "alwaysOn")}
                                                    >
                                                        {scheduleType !== "alwaysOn" ? "REMOVE SCHEDULE" : "SCHEDULE CONTENT"}
                                                    </SoftButton>
                                                )}
                                            </SoftBox>

                                            {scheduleType !== 'alwaysOn' && (
                                                <>
                                                    <SoftBox mt={2}>
                                                        <Divider />
                                                    </SoftBox>
                                                    <SoftBox borderRadius="10px" py={2} px={1} sx={{ backgroundColor: "#eeeeee" }}>
                                                        <SoftTypography pl={0.5} variant="body3" fontWeight='bold'>To Display</SoftTypography>
                                                        <Controller
                                                            name='scheduleType'
                                                            control={control}
                                                            render={({ field }) => (
                                                                <SoftBox mb={1} sx={{ borderRadius: '10px', backgroundColor: '#fff', width: '100%' }}>
                                                                    <SoftSelect
                                                                        value={schedule_options.find(opt => opt.value === field.value)}
                                                                        onChange={(selected) => field.onChange(selected.value)}
                                                                        options={schedule_options}
                                                                        isDisabled={!canCampaignsEdit}
                                                                    />
                                                                </SoftBox>

                                                            )}
                                                        />
                                                        <ScheduleInterface {...{ control, scheduleType, deviceLayout, canCampaignsEdit }} />
                                                    </SoftBox>
                                                </>
                                            )}
                                        </SoftBox>
                                    </Grid>
                                </Grid>
                            </SoftBox>

                            <SoftBox height="auto">
                                <Divider />
                                <SoftBox display='flex' justifyContent='space-between' px={3} py={2} sx={{ background: '#f9f9f9' }}>
                                    {canCampaignsEdit ? (
                                        <>
                                            <SoftButton variant="gradient" color="error" onClick={() => navigate(-1)}>Cancel</SoftButton>
                                            <SoftButton variant="gradient" color={appearance} onClick={triggerFormSubmit}>Save</SoftButton>
                                        </>
                                    ) : (
                                        <SoftButton variant="outlined" color={appearance} onClick={() => navigate(-1)}>Back</SoftButton>
                                    )}
                                </SoftBox>
                            </SoftBox>
                        </SoftBox>
                    </Card>
                </SoftBox>
            </>
        );
    }
}

CampaignEdit.propTypes = {
    campaign: PropTypes.object.isRequired,
    campaignTypes: PropTypes.array.isRequired,
    saveForm: PropTypes.func.isRequired,
    tagOptions: PropTypes.object,
};

export default CampaignEdit;